<template>
  <div class="container text-glow" ref="container">
    <picture-text-header :title="header" :parentGlitch="glitch" />
    <div :class="containerClass" class="picture-text-container">
      <div class="text-container">
        <text-typer class="description" :text="description" :parentGlitch="glitch" />
      </div>
      <div
        class="image-container open"
        data-aos="open"
        data-aos-anchor-placement="bottom-bottom"
        @click="openFullscreen"
      >
        <div class="picture-glitch-container">
          <div class="gradient-overlay"></div>
          <picture-glitch
            :parentGlitch="glitch"
            :src="imageSrc"
            :alt="imageAlt"
            :glitchSrc="glitchImageSrc"
          />
        </div>
        <!-- Corner picture expansions -->
        <template v-if="!left">
          <div class="top-left-corner corner">&boxdr;</div>
          <div class="bottom-right-corner corner">&boxul;</div>
        </template>
        <template v-else>
          <div class="top-right-corner corner">&boxdl;</div>
          <div class="bottom-left-corner corner">&boxur;</div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * Draws a picture with a text
 */
import TextTyper from "./text-typer"
import PictureTextHeader from "./picture-text-header"
import PictureGlitch from "./picture-glitch/picture-glitch"
import AsyncUtils from "@/scripts/utils/async"
import RandomUtils from "@/scripts/utils/random"
import { mapState } from "vuex"
export default {
  components: {
    TextTyper,
    PictureGlitch,
    PictureTextHeader
  },
  props: {
    header: String,
    description: String,
    left: Boolean,
    imageSrc: String,
    glitchImageSrc: String,
    imageAlt: String
  },
  data() {
    return {
      glitch: false,
      topPadding: 20,
      sidePadding: 40
    }
  },
  computed: {
    ...mapState({
      fullscreen: state => state.projectSynthecite.fullscreen,
      isMobile: state => state.isMobile,
    }),
    containerClass() {
      return {
        right: !this.left,
        left: this.left,
        hover: this.fullscreen.enabled && this.fullscreen.id === this.imageSrc
      }
    }
  },
  methods: {
    /**
     * Routinely causes text and picture to glitch
     * Tells children to glitch
     */
    async glitchRoutine() {
      const minWait = 15000
      const maxWait = 30000
      const minGlitchTime = 50
      const maxGlitchTime = 300
      const minIterations = 2
      const maxIterations = 3

      // helper func for glitch wait time
      let glitchWait = async () => {
        await AsyncUtils.Sleep(
          RandomUtils.RandomInt(minGlitchTime, maxGlitchTime)
        )
      }

      // eslint-disable-next-line
      while (true) {
        this.glitch = false
        let wait = RandomUtils.RandomNumber(minWait, maxWait)
        await AsyncUtils.Sleep(wait)
        let iterations = RandomUtils.RandomInt(minIterations, maxIterations)
        // randomly glitch out x amount of times
        for (let i = 0; i < iterations; i++) {
          this.glitch = true
          await glitchWait()
          this.glitch = false
          await glitchWait()
        }
      }
    },
    openFullscreen() {
      this.$store.commit("setFullscreen", {
        enabled: true,
        selectedIndex: 0,
        id: this.imageSrc,
        pictures: [this.imageSrc]
      })
    }
  },
  mounted() {
    // mobile glitches is too disorienting
    if (!this.isMobile) {
      this.glitchRoutine()
    }
  }
}
</script>

<style scoped lang="scss">
@import "@/views/project-synthecite/styles/variables.scss";

.seperator {
  width: 100%;
  text-align: center;
  margin-bottom: 50px;
  margin-top: 50px;
  font-size: 60px;
  white-space: nowrap;
  overflow: hidden;
}

.description {
  font-size: max(20px, min(30px, 4vw));
}

.picture-text-container {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.picture-text-container.left {
  padding-right: 30px;
}
.picture-text-container.right {
  flex-direction: row-reverse;
  padding-left: 30px;
}
.text-container {
  width: 50%;
  height: auto;
}

.image-container {
  width: 45%;
  position: relative;
  transition: 0.5s transform;
  cursor: pointer;
}
.image-container:hover,
.hover .image-container {
  transform: scale(1.1);
}
.gradient-overlay {
  position: absolute;
  top: 0;
  left: 0;
  z-index: 1;
  width: 100%;
  height: 100%;
  background: radial-gradient(transparent, rgba($green, 0.2));
}

// Responsive
@media only screen and (max-width: 900px) {
  .picture-text-container {
    flex-direction: column !important;
  }
  .picture-text-container.left,
  .picture-text-container.right {
    padding: 0px;
  }
  .text-container {
    width: 100%;
    margin-top: 5%;
    margin-bottom: 10%;
  }
  .image-container {
    width: 90%;
  }
}

.corner {
  position: absolute;
  font-size: 60px;
  line-height: 0px;
  line-height: 0px;
}

.picture-glitch-container,
.picture-glitch-container * {
  border-radius: 10px;
}

.image-container:hover .picture-glitch-container,
.image-container.hover .picture-glitch-container {
  box-shadow: 0px 0px 10px rgba($green, 0.5);
}

/* Scroll animations */

.picture-glitch-container,
.corner,
.gradient-overlay {
  transition: 0.5s all ease;
}
.image-container.open:not([data-aos="open"]),
[data-aos="open"].aos-animate {
  .gradient-overlay {
    opacity: 0;
  }

  .corner {
    opacity: 1;
    &.top-right-corner,
    &.top-left-corner {
      top: -20px;
    }
    &.bottom-right-corner,
    &.bottom-left-corner {
      bottom: -20px;
    }
    &.top-left-corner,
    &.bottom-left-corner {
      left: -40px;
    }
    &.top-right-corner,
    &.bottom-right-corner {
      right: -40px;
    }
  }

  .picture-glitch-container {
    transform: scale(1);
    transition-delay: 0.05s;
    opacity: 1;
    box-shadow: $text-shadow;
  }
}
[data-aos="open"] {
  .gradient-overlay {
    opacity: 1;
  }
  .corner {
    opacity: 0.5;
    &.top-right-corner,
    &.top-left-corner {
      top: calc(50% - 20px);
    }
    &.bottom-right-corner,
    &.bottom-left-corner {
      bottom: calc(50% - 20px);
    }
    &.top-left-corner,
    &.bottom-left-corner {
      left: calc(50% - 40px);
    }
    &.top-right-corner,
    &.bottom-right-corner {
      right: calc(50% - 40px);
    }
  }
  .picture-glitch-container {
    transform: scale(0);
    opacity: 1;
    box-shadow: 0px 0px 0px rgba($green, 0);
  }

  &.aos-animate {
  }
}
</style>
